import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Mail, CreditCard, Globe, Users, Zap, Shield,
  CheckCircle2, Ban, AlertCircle, Trash2, Plus, KeyRound, UserX,
  RefreshCw, Database, MoveRight, FileText, ExternalLink, Copy,
} from "lucide-react";
import { api } from "../../lib/auth";

const TABS = [
  { k: "overview", lbl: "Overview", icon: Building2 },
  { k: "users", lbl: "Users", icon: Users },
  { k: "modules", lbl: "Feature Access", icon: Zap },
  { k: "domains", lbl: "Domains", icon: Globe },
  { k: "subscription", lbl: "Subscription", icon: CreditCard },
  { k: "actions", lbl: "Admin Actions", icon: Shield },
];

function StatusPill({ status }) {
  const m = {
    active: "bg-[#E8F5E9] text-[#1FA868] ring-[#1FA868]/20",
    pending: "bg-[#FFF5D6] text-amber-700 ring-amber-500/20",
    rejected: "bg-rose-50 text-rose-600 ring-rose-500/20",
    suspended: "bg-slate-100 text-slate-700 ring-slate-500/20",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md ring-1 ring-inset ${m[status] || "bg-black/5 text-black/60"}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {status}
    </span>
  );
}

function KV({ lbl, value, mono, tone = "default" }) {
  const styles = {
    default: "bg-white",
    green: "bg-[#F4F8F3]",
    blue: "bg-[#EEF6FF]",
    amber: "bg-[#FFF5D6]",
    purple: "bg-[#F5F0FF]",
    rose: "bg-[#FFF1F2]",
  };

  return (
    <div
      className={`rounded-xl p-4 border border-black/5 shadow-sm hover:shadow-md transition ${styles[tone]}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-black/50 font-bold">
        {lbl}
      </div>
      <div className={`mt-1 text-sm font-semibold text-black truncate ${mono ? "font-mono text-xs" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

export default function AdminTenantDetail() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/tenants/${tenantId}`);
      setData(r.data);
    } catch (e) {
      toast.error("Tenant not found");
      nav("/admin/tenants");
    }
  };
  useEffect(() => { load(); api.get("/plans").then((r) => setPlans(r.data)); }, [tenantId]); // eslint-disable-line

  if (!data) return <div className="p-10 text-black/50">Loading tenant...</div>;

  const t = data.tenant;
  const users = data.users || [];
  const usage = data.usage || {};
  const allModules = data.all_modules || [];

  const changeStatus = async (status, reason = "") => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/status`, { status, reason });
      toast.success(`Tenant ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div data-testid="admin-tenant-detail" className="max-w-[1400px]">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-[#FFFCF2]/85 backdrop-blur-md border-b border-black/5">
        <div className="flex items-center gap-3 px-8 py-3">
          <Link to="/admin/tenants" className="inline-flex items-center gap-1.5 text-xs font-semibold text-black/60 hover:text-black px-2 py-1.5 rounded-lg hover:bg-black/5">
            <ArrowLeft size={13} /> Tenants
          </Link>
          <span className="text-black/20">/</span>
          <div className="font-bold text-sm truncate flex-1">{t.company_name}</div>
          <StatusPill status={t.status} />
        </div>
      </div>

      <div className="p-6 lg:p-8">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-[#1FA868] via-[#16834D] to-[#0E5934] rounded-3xl p-8 overflow-hidden">
          {/* Decorative grain */}
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 0%, white 0%, transparent 50%), radial-gradient(circle at 80% 100%, white 0%, transparent 50%)" }} />
          <div className="relative z-10 flex flex-wrap gap-6 items-start justify-between">
            <div className="flex items-start gap-5 min-w-0 flex-1">
              <div className="w-20 h-20 rounded-3xl bg-white/15 ring-2 ring-white/20 backdrop-blur text-white flex items-center justify-center font-display font-extrabold text-2xl tracking-tight">
                {(t.company_name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-white tracking-tight truncate">{t.company_name}</h1>
                <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-white/85">
                  <button onClick={() => copy(`${t.subdomain}.brivopos.com`)} data-testid="copy-subdomain"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/15 font-mono text-xs">
                    {t.subdomain}.brivo360.com <Copy size={11} />
                  </button>
                  <span className="inline-flex items-center gap-1.5"><Mail size={13} /> {t.admin_email}</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-3 py-1 text-xs font-bold text-white">
                  <CreditCard size={12} /> {t.plan_name} · {t.billing_cycle} · {t.amount ? `₦${Number(t.amount).toLocaleString()}` : "Free"}
                </div>
              </div>
            </div>
            {/* Action cluster */}
            <div className="flex items-center gap-2 flex-wrap">
              {t.status === "pending" && (
                <button onClick={() => changeStatus("active")} disabled={busy} data-testid="detail-approve"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#1FA868] text-sm font-bold hover:bg-white/95 shadow-sm">
                  <CheckCircle2 size={14} /> Approve
                </button>
              )}
              {t.status === "active" && (
                <button onClick={() => changeStatus("suspended")} disabled={busy} data-testid="detail-suspend"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white/10 ring-1 ring-white/30 text-white text-sm font-bold hover:bg-white/15">
                  <Ban size={14} /> Suspend
                </button>
              )}
              {t.status === "suspended" && (
                <button onClick={() => changeStatus("active")} disabled={busy} data-testid="detail-reactivate"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#1FA868] text-sm font-bold hover:bg-white/95 shadow-sm">
                  <CheckCircle2 size={14} /> Reactivate
                </button>
              )}
              <button onClick={() => {
                const r = window.prompt("Rejection reason?");
                if (r !== null) changeStatus("rejected", r);
              }} disabled={busy} data-testid="detail-reject"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-500/15 ring-1 ring-rose-300/40 text-white text-sm font-bold hover:bg-rose-500/25">
                <AlertCircle size={14} /> Reject
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="relative z-10 mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { lbl: "Status", v: t.status, mono: false },
              { lbl: "Modules", v: (t.modules_enabled || []).length },
              { lbl: "Created", v: (t.created_at || "").slice(0, 10) },
              { lbl: "Approved", v: (t.approved_at || "—").slice(0, 10) },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-3 ring-1 ring-white/15">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/70">{s.lbl}</div>
                <div className="text-sm font-bold text-white mt-0.5 truncate capitalize">{s.v || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 bg-white border border-black/8 rounded-2xl overflow-hidden">
          <div className="flex gap-1 px-3 pt-3 border-b border-black/8 overflow-x-auto sticky top-[52px] bg-white z-20">
            {TABS.map((x) => {
              const Icon = x.icon;
              const active = tab === x.k;
            return (
              <button key={x.k} onClick={() => setTab(x.k)}
                data-testid={`tab-${x.k}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-t-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                  active ? "bg-[#FFFCF2] text-[#1B4332] border-b-2 border-[#1B4332] -mb-px" : "text-black/60 hover:bg-black/[0.03]"
                }`}>
                <Icon size={14} /> {x.lbl}
              </button>
            );
          })}
        </div>

        <div className="p-6 lg:p-7">
          {tab === "overview" && <OverviewTab tenant={t} usage={usage} users={users} />}
          {tab === "users" && <UsersTab users={users} tenantId={tenantId} onChanged={load} />}
          {tab === "modules" && <ModulesTab tenant={t} allModules={allModules} tenantId={tenantId} onChanged={load} />}
          {tab === "domains" && <DomainsTab tenant={t} tenantId={tenantId} onChanged={load} />}
          {tab === "subscription" && <SubscriptionTab tenant={t} plans={plans} tenantId={tenantId} onChanged={load} />}
          {tab === "actions" && <ActionsTab tenantId={tenantId} onChanged={load} nav={nav} />}
        </div>
      </div>
      </div>
    </div>
  );
}

function OverviewTab({ tenant: t, usage, users }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-base text-black mb-3">Tenant details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KV lbl="Company" value={t.company_name} tone="green" />
<KV lbl="Subdomain" value={t.subdomain} mono tone="blue" />
<KV lbl="Admin email" value={t.admin_email} tone="default" />

<KV lbl="Plan" value={t.plan_name} tone="purple" />
<KV lbl="Billing" value={t.billing_cycle} tone="blue" />
<KV lbl="Amount" value={`₦${Number(t.amount || 0).toLocaleString()}`} tone="green" />

<KV lbl="Payment status" value={t.payment_status} tone="amber" />
<KV lbl="Payment method" value={t.payment_method || "—"} tone="default" />
<KV lbl="Payment ref" value={t.payment_reference} mono tone="blue" />

<KV lbl="Created" value={(t.created_at || "").slice(0, 10)} tone="default" />
<KV lbl="Approved" value={(t.approved_at || "").slice(0, 10) || "—"} tone="green" />
<KV lbl="Suspended" value={(t.suspended_at || "").slice(0, 10) || "—"} tone="rose" />
        </div>
      </div>

      {t.rejection_reason && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-rose-600">Rejection reason</div>
          <div className="text-sm text-rose-700 mt-1">{t.rejection_reason}</div>
        </div>
      )}

      {t.payment_proof_url && (
        <div>
          <h3 className="font-display font-bold text-base text-black mb-3">Payment Proof</h3>
          <a href={`${process.env.REACT_APP_BACKEND_URL}${t.payment_proof_url}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-xl border border-black/10 p-4 hover:bg-[#FFFCF2]">
            <FileText size={20} className="text-[#1FA868]" />
            <div>
              <div className="font-semibold text-black text-sm">{t.payment_proof_filename || "proof"}</div>
              <div className="text-[11px] text-black/50">Click to open</div>
            </div>
            <ExternalLink size={14} className="text-black/40 ml-2" />
          </a>
        </div>
      )}

      <div>
        <h3 className="font-display font-bold text-base text-black mb-3">Usage</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KV lbl="Users" value={usage.users || users.length} />
          <KV lbl="Warehouses" value={usage.warehouses ?? 1} />
          <KV lbl="Products" value={usage.products ?? 0} />
          <KV lbl="Customers" value={usage.customers ?? 0} />
          <KV lbl="Suppliers" value={usage.suppliers ?? 0} />
        </div>
      </div>
    </div>
  );
}

function UsersTab({ users, tenantId, onChanged }) {
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");

  const toggleBlock = async (u) => {
    try {
      await api.post(`/admin/tenants/${tenantId}/users/${u.id}/toggle-block`);
      toast.success(u.is_active ? "User blocked" : "User unblocked");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changePw = async () => {
    if (pw.length < 8) return toast.error("8+ chars required");
    try {
      await api.post(`/admin/tenants/${tenantId}/users/${pwFor.id}/change-password`, { new_password: pw });
      toast.success("Password changed");
      setPwFor(null); setPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <h3 className="font-display font-bold text-base text-black mb-3">Tenant users</h3>
      <div className="overflow-hidden rounded-xl border border-black/8">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFCF2] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3 font-bold">Name</th>
              <th className="text-left p-3 font-bold">Email</th>
              <th className="text-left p-3 font-bold">Role</th>
              <th className="text-left p-3 font-bold">Status</th>
              <th className="text-left p-3 font-bold">Last login</th>
              <th className="text-right p-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/8">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="p-3 font-semibold text-black">{u.name}</td>
                <td className="p-3 text-black/70">{u.email}</td>
                <td className="p-3 text-black/70 capitalize">{u.role?.replace("_", " ")}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${u.is_active ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-rose-50 text-rose-600"}`}>
                    {u.is_active ? "active" : "blocked"}
                  </span>
                </td>
                <td className="p-3 text-black/60 text-xs">{(u.last_login_at || "").slice(0, 10) || "—"}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setPwFor(u)} data-testid={`user-changepw-${u.email}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-white border border-black/10 hover:border-black/25">
                      <KeyRound size={12} /> Password
                    </button>
                    <button onClick={() => toggleBlock(u)} data-testid={`user-toggle-${u.email}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-md ${
                        u.is_active ? "bg-rose-50 text-rose-600 hover:bg-rose-100" : "bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D9EEDA]"
                      }`}>
                      <UserX size={12} /> {u.is_active ? "Block" : "Unblock"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-black/50">No users</td></tr>}
          </tbody>
        </table>
      </div>

      {pwFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPwFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-black">Change password</h3>
            <p className="text-xs text-black/55 mt-0.5">For {pwFor.email}</p>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
              data-testid="user-pw-input" placeholder="New password (8+ chars)"
              className="mt-4 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPwFor(null)} className="px-3 py-2 rounded-lg border border-black/15 text-sm">Cancel</button>
              <button onClick={changePw} data-testid="user-pw-save"
                className="px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-semibold">Change</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModulesTab({ tenant, allModules, tenantId, onChanged }) {
  const [selected, setSelected] = useState(tenant.modules_enabled || []);
  const [busy, setBusy] = useState(false);

  const toggle = (m) => setSelected((s) => s.includes(m) ? s.filter((x) => x !== m) : [...s, m]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/modules`, selected);
      toast.success("Modules updated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-base text-black">Feature Access</h3>
          <p className="text-xs text-black/55 mt-0.5">Toggle which modules this tenant can access.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="modules-save"
          className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124] disabled:opacity-60">
          Save changes
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {allModules.map((m) => {
          const on = selected.includes(m);
          return (
            <label key={m} data-testid={`module-toggle-${m}`}
              className={`flex items-center justify-between gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                on ? "bg-[#F4F8F3] border-[#1B4332]/30" : "bg-white border-black/10 hover:border-black/20"
              }`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${on ? "bg-[#1B4332] text-white" : "bg-black/5 text-black/50"}`}>
                  <Zap size={16} />
                </div>
                <div className="font-semibold text-sm text-black truncate">{m}</div>
              </div>
              <input type="checkbox" checked={on} onChange={() => toggle(m)} className="w-4 h-4 accent-[#1B4332]" />
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DomainsTab({ tenant, tenantId, onChanged }) {
  const [val, setVal] = useState("");
  const [kind, setKind] = useState("subdomain");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!val) return;
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/domains`, { domain: val, kind });
      toast.success("Domain added");
      setVal("");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this domain?")) return;
    try {
      await api.delete(`/admin/tenants/${tenantId}/domains/${id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const domains = tenant.domains || [];
  return (
    <div>
      <h3 className="font-display font-bold text-base text-black mb-3">Domains</h3>
      <form onSubmit={add} className="flex flex-wrap items-end gap-2 bg-[#FFFCF2] p-4 rounded-xl border border-black/8 mb-4">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Domain</label>
          <input value={val} onChange={(e) => setVal(e.target.value)} data-testid="domain-input"
            placeholder="e.g. acme or shop.acme.com"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm bg-white" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Kind</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="domain-kind"
            className="mt-1 px-3 py-2 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm bg-white">
            <option value="subdomain">Subdomain</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <button disabled={busy} data-testid="domain-add"
          className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124] inline-flex items-center gap-1.5">
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="space-y-2">
        {domains.map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 bg-white border border-black/8 rounded-xl p-3.5">
            <div className="flex items-center gap-3 min-w-0">
              <Globe size={16} className="text-black/50" />
              <div className="min-w-0">
                <div className="font-mono text-sm text-black truncate">{d.value}</div>
                <div className="text-[11px] text-black/50 capitalize">{d.kind}</div>
              </div>
            </div>
            <button onClick={() => remove(d.id)} data-testid={`domain-remove-${d.id}`}
              className="p-2 rounded-md hover:bg-rose-50 text-rose-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {domains.length === 0 && <div className="text-sm text-black/50 text-center py-6">No domains yet.</div>}
      </div>
    </div>
  );
}

function SubscriptionTab({ tenant, plans, tenantId, onChanged }) {
  const [planId, setPlanId] = useState(tenant.plan_id);
  const [cycle, setCycle] = useState(tenant.billing_cycle);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${tenantId}`, { plan_id: planId, billing_cycle: cycle });
      toast.success("Subscription updated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const plan = plans.find((p) => p.id === planId);
  const amt = plan ? (cycle === "yearly" ? plan.price_yearly : plan.price_monthly) : tenant.amount;

  return (
    <div className="space-y-5">
      <h3 className="font-display font-bold text-base text-black">Subscription</h3>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Plan</label>
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="sub-plan"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 bg-white focus:border-[#1B4332] outline-none text-sm">
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ₦{Number(p.price_monthly).toLocaleString()}/mo</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Billing cycle</label>
          <select value={cycle} onChange={(e) => setCycle(e.target.value)} data-testid="sub-cycle"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-black/15 bg-white focus:border-[#1B4332] outline-none text-sm">
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-black/10 bg-[#FFFCF2] p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-black/55 font-bold">New amount</div>
          <div className="font-display font-extrabold text-2xl text-black mt-1">₦{Number(amt || 0).toLocaleString()}</div>
          <div className="text-xs text-black/55">{cycle === "yearly" ? "billed annually" : "billed monthly"}</div>
        </div>
        <button onClick={save} disabled={busy} data-testid="sub-save"
          className="px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124] disabled:opacity-60">
          Save changes
        </button>
      </div>
    </div>
  );
}

function ActionsTab({ tenantId, onChanged, nav }) {
  const [busy, setBusy] = useState("");

  const run = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(action);
    try {
      const r = await api.post(`/admin/tenants/${tenantId}/admin-action`, null, { params: { action } });
      toast.success(r.data?.message || "Done");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const hardDelete = async () => {
    if (!window.confirm("PERMANENTLY delete this tenant and all its data? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/tenants/${tenantId}`);
      toast.success("Tenant deleted");
      nav("/admin/tenants");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const items = [
    { k: "reseed", lbl: "Re-seed Sample Data", desc: "Reset & reload mock clients / revenue / expenses.", icon: Database, tone: "emerald" },
    { k: "reprovision", lbl: "Re-provision", desc: "Re-create tenant infrastructure hooks (simulated).", icon: RefreshCw, tone: "sky" },
    { k: "migrate", lbl: "Run Migrations", desc: "Apply schema migrations for this tenant.", icon: MoveRight, tone: "violet" },
    { k: "reset_db", lbl: "Reset Tenant Data", desc: "Wipe all tenant data (not users). Dangerous.", icon: AlertCircle, tone: "amber", confirm: "Wipe all tenant data?" },
  ];

  return (
    <div className="space-y-5">
      <h3 className="font-display font-bold text-base text-black">Admin Actions</h3>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          const colors = {
            emerald: "bg-[#E8F5E9] text-[#1FA868]",
            sky: "bg-[#E0F2FE] text-sky-700",
            violet: "bg-[#EEEBFA] text-violet-700",
            amber: "bg-[#FFF5D6] text-amber-700",
          }[it.tone];
          return (
            <button key={it.k} onClick={() => run(it.k, it.confirm)} disabled={busy === it.k}
              data-testid={`action-${it.k}`}
              className="text-left bg-white border border-black/10 rounded-xl p-4 hover:border-black/25 transition-colors disabled:opacity-60">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg ${colors} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={16} />
                </div>
                <div>
                  <div className="font-semibold text-black text-sm">{it.lbl}</div>
                  <div className="text-xs text-black/55 mt-0.5">{it.desc}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="border-t border-black/8 pt-5">
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-semibold text-rose-700 text-sm">Delete tenant permanently</div>
            <div className="text-xs text-rose-600/80">Removes tenant, users, and all associated data. Cannot be undone.</div>
          </div>
          <button onClick={hardDelete} data-testid="action-hard-delete"
            className="px-3.5 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 inline-flex items-center gap-1.5">
            <Trash2 size={14} /> Delete Tenant
          </button>
        </div>
      </div>
    </div>
  );
}
